import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  HandCoins,
  Storefront,
  Bank,
  Headset,
  CheckCircle,
  Sparkle,
  ArrowRight,
  Receipt,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import PageHero from "../components/site/PageHero";
import { submitPartnerLead } from "../lib/publicApi";

const benefits = [
  {
    Icon: HandCoins,
    title: "Earn on every transaction",
    text: "Transparent, per-service commission visible inside the retailer app — see what you earn before you transact.",
  },
  {
    Icon: Storefront,
    title: "One app, many services",
    text: "Recharge, Bills, PAN, NSDL BC, RD live today. IRCTC, AePS, Loans and more on the roadmap.",
  },
  {
    Icon: Bank,
    title: "Multiple settlement methods",
    text: "Wallet-to-Bank transfer, settlement & refunds via your saved Bank Master.",
  },
  {
    Icon: Headset,
    title: "Real human support",
    text: "Call-back, support tickets, customer care and distributor contact — all in-app.",
  },
];

const steps = [
  { num: "01", title: "Apply", text: "Fill the partner form and submit." },
  { num: "02", title: "KYC", text: "Aadhaar eKYC, PAN, bank & documents." },
  { num: "03", title: "Package", text: "Pick the service package that fits." },
  { num: "04", title: "Go Live", text: "Add money and start transacting." },
];

const businessTypes = [
  { label: "Mobile / Recharge Shop", value: "MOBILE_RECHARGE_SHOP" },
  { label: "Kirana / General Store", value: "KIRANA_GENERAL_STORE" },
  { label: "CSC / Banking Correspondent", value: "CSC_BC" },
  {
    label: "Distributor / Master Distributor",
    value: "DISTRIBUTOR_MASTER_DISTRIBUTOR",
  },
  { label: "Other", value: "OTHER" },
];

const fieldMap = {
  name: "full_name",
  shop: "shop_business_name",
  email: "email_address",
  phone: "mobile_number",
  city: "city",
  state: "state",
  type: "business_type",
  notes: "additional_message",
};

const getFieldError = (errors, field) => {
  const error = errors[fieldMap[field]];

  if (Array.isArray(error)) {
    return error.join(" ");
  }

  return error;
};

export default function BecomePartner() {
  const [form, setForm] = useState({
    name: "",
    shop: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    type: "MOBILE_RECHARGE_SHOP",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const onChange = (k, v) => {
    setSubmitMessage("");
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[fieldMap[k]];
      return next;
    });
    setForm((p) => ({ ...p, [k]: v }));
  };

  const resetForm = () => {
    setForm({
      name: "",
      shop: "",
      email: "",
      phone: "",
      city: "",
      state: "",
      type: "MOBILE_RECHARGE_SHOP",
      notes: "",
    });
    setSubmitMessage("");
    setFieldErrors({});
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitMessage("");
    setFieldErrors({});

    try {
      const response = await submitPartnerLead(form);
      setSubmitMessage(response.message || "");
      setSubmitted(true);
    } catch (error) {
      setSubmitMessage(error.message);
      setFieldErrors(error.errors || {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partner-page">
      <PageHero
        eyebrow="Become a JayesPay Partner"
        title="Turn your shop into a"
        highlight="digital service counter"
        description="Onboard digitally, complete KYC, activate a package, and start serving customers within minutes — with a transparent, per-service commission structure."
        testId="partner-hero"
      />

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-surfaceAlt border border-line rounded-3xl p-6 hover:-translate-y-1 hover:border-brandBlue/40 hover:shadow-lg transition-all"
                data-testid={`partner-benefit-${i}`}
              >
                <div className="h-12 w-12 rounded-2xl brand-icon-tile flex items-center justify-center mb-4">
                  <b.Icon size={22} weight="duotone" />
                </div>
                <h3 className="font-heading font-bold text-ink900 text-lg tracking-tight mb-2">
                  {b.title}
                </h3>
                <p className="text-sm text-ink700 leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 bg-surfaceAlt border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-3xl mb-10">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-3">
              How to join
            </div>
            <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl tracking-tight">
              Four steps to go live.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="relative bg-surface border border-line rounded-2xl p-5"
                data-testid={`partner-step-${s.num}`}
              >
                <div className="font-heading font-black text-5xl tracking-tighter text-brandBlue/30 mb-2">
                  {s.num}
                </div>
                <div className="text-sm font-bold text-ink900 mb-1">{s.title}</div>
                <div className="text-xs text-ink700">{s.text}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="bg-surfaceAlt border border-line rounded-3xl p-6 sm:p-12">
            {submitted ? (
              <div data-testid="partner-success-state" className="text-center py-12">
                <div className="mx-auto h-20 w-20 rounded-full bg-brandBlue/20 flex items-center justify-center mb-5">
                  <CheckCircle size={40} weight="fill" className="text-infoText" />
                </div>
                <h3 className="font-heading font-extrabold text-ink900 text-2xl sm:text-3xl tracking-tight mb-2">
                  Application received!
                </h3>
                <p className="text-ink700 max-w-md mx-auto mb-6">
                  {submitMessage ||
                    "Thank you for choosing JayesPay. Our onboarding team will reach out shortly to begin your KYC and activation."}
                </p>
                <Button
                  data-testid="partner-submit-another"
                  onClick={() => {
                    resetForm();
                    setSubmitted(false);
                  }}
                  className="bg-brandNavy text-surfaceAlt hover:bg-brandBlue rounded-full px-6"
                >
                  Submit another
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-1">
                  <Sparkle size={14} weight="fill" className="text-brandBlue" />
                  Partner application
                </div>
                <h2 className="font-heading font-extrabold text-ink900 text-2xl sm:text-3xl tracking-tight">
                  Tell us about your business.
                </h2>

                {submitMessage ? (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {submitMessage}
                  </div>
                ) : null}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="p-name" className="text-ink700 mb-1.5 block">
                      Full name
                    </Label>
                    <Input
                      id="p-name"
                      data-testid="partner-name"
                      required
                      value={form.name}
                      onChange={(e) => onChange("name", e.target.value)}
                      placeholder="Your full name"
                      className="bg-surface border-line"
                    />
                    {getFieldError(fieldErrors, "name") ? (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {getFieldError(fieldErrors, "name")}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="p-shop" className="text-ink700 mb-1.5 block">
                      Shop / Business name
                    </Label>
                    <Input
                      id="p-shop"
                      data-testid="partner-shop"
                      required
                      value={form.shop}
                      onChange={(e) => onChange("shop", e.target.value)}
                      placeholder="e.g. Sharma Mobile Shop"
                      className="bg-surface border-line"
                    />
                    {getFieldError(fieldErrors, "shop") ? (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {getFieldError(fieldErrors, "shop")}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="p-email" className="text-ink700 mb-1.5 block">
                      Email
                    </Label>
                    <Input
                      id="p-email"
                      data-testid="partner-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => onChange("email", e.target.value)}
                      placeholder="you@example.com"
                      className="bg-surface border-line"
                    />
                    {getFieldError(fieldErrors, "email") ? (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {getFieldError(fieldErrors, "email")}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="p-phone" className="text-ink700 mb-1.5 block">
                      Mobile
                    </Label>
                    <Input
                      id="p-phone"
                      data-testid="partner-phone"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(e) => onChange("phone", e.target.value)}
                      placeholder="+91 9xxxx xxxxx"
                      className="bg-surface border-line"
                    />
                    {getFieldError(fieldErrors, "phone") ? (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {getFieldError(fieldErrors, "phone")}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="p-city" className="text-ink700 mb-1.5 block">
                      City
                    </Label>
                    <Input
                      id="p-city"
                      data-testid="partner-city"
                      required
                      value={form.city}
                      onChange={(e) => onChange("city", e.target.value)}
                      placeholder="City"
                      className="bg-surface border-line"
                    />
                    {getFieldError(fieldErrors, "city") ? (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {getFieldError(fieldErrors, "city")}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="p-state" className="text-ink700 mb-1.5 block">
                      State
                    </Label>
                    <Input
                      id="p-state"
                      data-testid="partner-state"
                      required
                      value={form.state}
                      onChange={(e) => onChange("state", e.target.value)}
                      placeholder="State"
                      className="bg-surface border-line"
                    />
                    {getFieldError(fieldErrors, "state") ? (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {getFieldError(fieldErrors, "state")}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <Label className="text-ink700 mb-1.5 block">Business type</Label>
                  <Select value={form.type} onValueChange={(v) => onChange("type", v)}>
                    <SelectTrigger
                      data-testid="partner-type"
                      className="bg-surface border-line"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {businessTypes.map((t) => (
                        <SelectItem
                          key={t.value}
                          value={t.value}
                          data-testid={`type-${t.value}`}
                        >
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {getFieldError(fieldErrors, "type") ? (
                    <p className="mt-1.5 text-xs font-medium text-red-600">
                      {getFieldError(fieldErrors, "type")}
                    </p>
                  ) : null}
                </div>

                <div>
                  <Label htmlFor="p-notes" className="text-ink700 mb-1.5 block">
                    Anything we should know? (optional)
                  </Label>
                  <Textarea
                    id="p-notes"
                    data-testid="partner-notes"
                    rows={4}
                    value={form.notes}
                    onChange={(e) => onChange("notes", e.target.value)}
                    placeholder="Tell us about your current services, footfall, etc."
                    className="bg-surface border-line resize-none"
                  />
                  {getFieldError(fieldErrors, "notes") ? (
                    <p className="mt-1.5 text-xs font-medium text-red-600">
                      {getFieldError(fieldErrors, "notes")}
                    </p>
                  ) : null}
                </div>

                <Button
                  type="submit"
                  data-testid="partner-submit"
                  disabled={loading}
                  className="btn-shimmer w-full bg-brandOrange text-ink900 hover:bg-brandBlue hover:text-surfaceAlt rounded-full h-12 text-sm font-bold"
                >
                  {loading ? "Submitting..." : "Submit application"}
                  <ArrowRight size={16} weight="bold" className="ml-2" />
                </Button>

                <p className="text-xs text-ink500 text-center flex items-center justify-center gap-1.5">
                  <Receipt size={14} weight="duotone" /> Approval typically
                  within 24 – 48 hours of complete KYC.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
